import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, BookHeart, Plus, Calendar as CalendarIcon, Clock, 
    Loader2, CheckCircle2, AlertCircle, Droplets, Activity, 
    Pill, Bell, Trash2, Edit2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const diaryTypes = [
    { id: 'menstrual', name: 'Menstrual Cycle', icon: Droplets, color: 'text-pink-500', bgColor: 'bg-pink-100 dark:bg-pink-900/30' },
    { id: 'pain', name: 'Pain Diary', icon: Activity, color: 'text-orange-500', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
    { id: 'medication', name: 'Medication', icon: Pill, color: 'text-blue-500', bgColor: 'bg-blue-100 dark:bg-blue-900/30' }
];

const painLevels = [
    { value: '1', label: '1 - Minimal' },
    { value: '2', label: '2 - Mild' },
    { value: '3', label: '3 - Uncomfortable' },
    { value: '4', label: '4 - Moderate' },
    { value: '5', label: '5 - Distracting' },
    { value: '6', label: '6 - Distressing' },
    { value: '7', label: '7 - Severe' },
    { value: '8', label: '8 - Intense' },
    { value: '9', label: '9 - Excruciating' },
    { value: '10', label: '10 - Unbearable' }
];

const flowLevels = [
    { value: 'spotting', label: 'Spotting' },
    { value: 'light', label: 'Light' },
    { value: 'medium', label: 'Medium' },
    { value: 'heavy', label: 'Heavy' },
    { value: 'very_heavy', label: 'Very Heavy' }
];

const medicationFrequencies = [
    { value: 'once_daily', label: 'Once daily' },
    { value: 'twice_daily', label: 'Twice daily' },
    { value: 'three_daily', label: 'Three times daily' },
    { value: 'four_daily', label: 'Four times daily' },
    { value: 'as_needed', label: 'As needed' },
    { value: 'weekly', label: 'Weekly' }
];

export default function HealthDiary() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [showAddEntry, setShowAddEntry] = useState(false);
    const [showAddReminder, setShowAddReminder] = useState(false);
    const [activeTab, setActiveTab] = useState('menstrual');
    const [selectedDate, setSelectedDate] = useState(new Date());
    
    // Form states
    const [menstrualForm, setMenstrualForm] = useState({
        flow_level: '',
        symptoms: '',
        mood: '',
        notes: ''
    });
    
    const [painForm, setPainForm] = useState({
        pain_level: '',
        location: '',
        description: '',
        triggers: '',
        relief_measures: '',
        notes: ''
    });
    
    const [medicationForm, setMedicationForm] = useState({
        medication_name: '',
        dosage: '',
        frequency: '',
        time_taken: '',
        side_effects: '',
        notes: ''
    });
    
    const [reminderForm, setReminderForm] = useState({
        reminder_type: 'medication',
        title: '',
        time: '',
        days: [],
        notes: ''
    });

    useEffect(() => {
        fetchEntries();
        fetchReminders();
    }, [activeTab]);

    const fetchEntries = async () => {
        try {
            const response = await axios.get(`${API}/health-diary?entry_type=${activeTab}`);
            setEntries(response.data);
        } catch (err) {
            console.error('Failed to fetch diary entries');
        }
    };

    const fetchReminders = async () => {
        try {
            const response = await axios.get(`${API}/health-diary/reminders`);
            setReminders(response.data);
        } catch (err) {
            console.error('Failed to fetch reminders');
        }
    };

    const handleSubmitEntry = async () => {
        let data = {};
        
        if (activeTab === 'menstrual') {
            if (!menstrualForm.flow_level) {
                toast.error('Please select flow level');
                return;
            }
            data = { ...menstrualForm };
        } else if (activeTab === 'pain') {
            if (!painForm.pain_level || !painForm.location) {
                toast.error('Please fill in pain level and location');
                return;
            }
            data = { ...painForm };
        } else if (activeTab === 'medication') {
            if (!medicationForm.medication_name || !medicationForm.dosage) {
                toast.error('Please fill in medication name and dosage');
                return;
            }
            data = { ...medicationForm };
        }

        setLoading(true);
        try {
            await axios.post(`${API}/health-diary`, {
                entry_type: activeTab,
                date: selectedDate.toISOString().split('T')[0],
                data: data,
                notes: data.notes || ''
            });
            toast.success('Entry added successfully!');
            setShowAddEntry(false);
            resetForms();
            fetchEntries();
        } catch (err) {
            toast.error('Failed to add entry');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitReminder = async () => {
        if (!reminderForm.title || !reminderForm.time) {
            toast.error('Please fill in title and time');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/health-diary/reminders`, reminderForm);
            toast.success('Reminder created successfully!');
            setShowAddReminder(false);
            setReminderForm({ reminder_type: 'medication', title: '', time: '', days: [], notes: '' });
            fetchReminders();
        } catch (err) {
            toast.error('Failed to create reminder');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        try {
            await axios.delete(`${API}/health-diary/${entryId}`);
            toast.success('Entry deleted');
            fetchEntries();
        } catch (err) {
            toast.error('Failed to delete entry');
        }
    };

    const handleDeleteReminder = async (reminderId) => {
        try {
            await axios.delete(`${API}/health-diary/reminders/${reminderId}`);
            toast.success('Reminder deleted');
            fetchReminders();
        } catch (err) {
            toast.error('Failed to delete reminder');
        }
    };

    const resetForms = () => {
        setMenstrualForm({ flow_level: '', symptoms: '', mood: '', notes: '' });
        setPainForm({ pain_level: '', location: '', description: '', triggers: '', relief_measures: '', notes: '' });
        setMedicationForm({ medication_name: '', dosage: '', frequency: '', time_taken: '', side_effects: '', notes: '' });
    };

    const getDiaryTypeInfo = (type) => diaryTypes.find(t => t.id === type) || diaryTypes[0];

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="health-diary-page">
            <div className="max-w-4xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToDashboard') || 'Back to Dashboard'}
                </Button>
                
                {/* Centered Header Section */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <BookHeart className="w-5 h-5 text-primary" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold">{t('healthDiary')}</h1>
                    </div>
                    <p className="text-muted-foreground mb-4">{t('trackHealthPatterns') || 'Track your health patterns and set medication reminders'}</p>
                    <div className="flex items-center justify-center gap-3">
                        <Button onClick={() => setShowAddEntry(true)} className="rounded-full">
                            <Plus className="w-4 h-4 mr-2" /> {t('addEntry') || 'Add Entry'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowAddReminder(true)} className="rounded-full">
                            <Bell className="w-4 h-4 mr-2" /> {t('setReminder') || 'Set Reminder'}
                        </Button>
                    </div>
                </div>

                {/* Diary Type Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
                    <TabsList className="grid grid-cols-3 w-full">
                        {diaryTypes.map((type) => {
                            const Icon = type.icon;
                            return (
                                <TabsTrigger key={type.id} value={type.id} className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 ${type.color}`} />
                                    <span className="hidden sm:inline">{type.name}</span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </Tabs>

                {/* Add Entry Modal */}
                {showAddEntry && (
                    <Card className="mb-8 border-2 border-primary/20">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    {(() => {
                                        const typeInfo = getDiaryTypeInfo(activeTab);
                                        const Icon = typeInfo.icon;
                                        return <Icon className={`w-5 h-5 ${typeInfo.color}`} />;
                                    })()}
                                    Add {getDiaryTypeInfo(activeTab).name} Entry
                                </CardTitle>
                                <CardDescription>Record your health data for tracking</CardDescription>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setShowAddEntry(false)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Date Selection */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label>Select Date</Label>
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={(date) => date && setSelectedDate(date)}
                                        className="rounded-md border"
                                    />
                                </div>

                                <div className="space-y-4">
                                    {/* Menstrual Form */}
                                    {activeTab === 'menstrual' && (
                                        <>
                                            <div className="space-y-2">
                                                <Label>Flow Level *</Label>
                                                <Select value={menstrualForm.flow_level} onValueChange={(v) => setMenstrualForm({...menstrualForm, flow_level: v})}>
                                                    <SelectTrigger><SelectValue placeholder="Select flow level" /></SelectTrigger>
                                                    <SelectContent>
                                                        {flowLevels.map(f => (
                                                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Symptoms</Label>
                                                <Input 
                                                    value={menstrualForm.symptoms}
                                                    onChange={(e) => setMenstrualForm({...menstrualForm, symptoms: e.target.value})}
                                                    placeholder="e.g., cramps, bloating, headache..."
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Mood</Label>
                                                <Input 
                                                    value={menstrualForm.mood}
                                                    onChange={(e) => setMenstrualForm({...menstrualForm, mood: e.target.value})}
                                                    placeholder="How are you feeling today?"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Notes</Label>
                                                <Textarea 
                                                    value={menstrualForm.notes}
                                                    onChange={(e) => setMenstrualForm({...menstrualForm, notes: e.target.value})}
                                                    placeholder="Any additional notes..."
                                                    rows={2}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Pain Form */}
                                    {activeTab === 'pain' && (
                                        <>
                                            <div className="space-y-2">
                                                <Label>Pain Level (1-10) *</Label>
                                                <Select value={painForm.pain_level} onValueChange={(v) => setPainForm({...painForm, pain_level: v})}>
                                                    <SelectTrigger><SelectValue placeholder="Select pain level" /></SelectTrigger>
                                                    <SelectContent>
                                                        {painLevels.map(p => (
                                                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Location *</Label>
                                                <Input 
                                                    value={painForm.location}
                                                    onChange={(e) => setPainForm({...painForm, location: e.target.value})}
                                                    placeholder="e.g., lower back, head, knee..."
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Input 
                                                    value={painForm.description}
                                                    onChange={(e) => setPainForm({...painForm, description: e.target.value})}
                                                    placeholder="e.g., sharp, dull, throbbing..."
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Triggers</Label>
                                                <Input 
                                                    value={painForm.triggers}
                                                    onChange={(e) => setPainForm({...painForm, triggers: e.target.value})}
                                                    placeholder="What triggered the pain?"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Relief Measures</Label>
                                                <Input 
                                                    value={painForm.relief_measures}
                                                    onChange={(e) => setPainForm({...painForm, relief_measures: e.target.value})}
                                                    placeholder="What helped relieve the pain?"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Medication Form */}
                                    {activeTab === 'medication' && (
                                        <>
                                            <div className="space-y-2">
                                                <Label>Medication Name *</Label>
                                                <Input 
                                                    value={medicationForm.medication_name}
                                                    onChange={(e) => setMedicationForm({...medicationForm, medication_name: e.target.value})}
                                                    placeholder="Enter medication name"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Dosage *</Label>
                                                <Input 
                                                    value={medicationForm.dosage}
                                                    onChange={(e) => setMedicationForm({...medicationForm, dosage: e.target.value})}
                                                    placeholder="e.g., 500mg, 2 tablets..."
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Frequency</Label>
                                                <Select value={medicationForm.frequency} onValueChange={(v) => setMedicationForm({...medicationForm, frequency: v})}>
                                                    <SelectTrigger><SelectValue placeholder="Select frequency" /></SelectTrigger>
                                                    <SelectContent>
                                                        {medicationFrequencies.map(f => (
                                                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Time Taken</Label>
                                                <Input 
                                                    type="time"
                                                    value={medicationForm.time_taken}
                                                    onChange={(e) => setMedicationForm({...medicationForm, time_taken: e.target.value})}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Side Effects</Label>
                                                <Input 
                                                    value={medicationForm.side_effects}
                                                    onChange={(e) => setMedicationForm({...medicationForm, side_effects: e.target.value})}
                                                    placeholder="Any side effects experienced?"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowAddEntry(false)}>Cancel</Button>
                                <Button onClick={handleSubmitEntry} disabled={loading} className="rounded-full">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    Save Entry
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Add Reminder Modal */}
                {showAddReminder && (
                    <Card className="mb-8 border-2 border-amber-200 dark:border-amber-800">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-amber-500" />
                                    Set Reminder
                                </CardTitle>
                                <CardDescription>Create alerts for medications and health tracking</CardDescription>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setShowAddReminder(false)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Reminder Type</Label>
                                    <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm({...reminderForm, reminder_type: v})}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="medication">Medication</SelectItem>
                                            <SelectItem value="menstrual">Menstrual Cycle</SelectItem>
                                            <SelectItem value="pain_check">Pain Check-in</SelectItem>
                                            <SelectItem value="appointment">Appointment</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Title *</Label>
                                    <Input 
                                        value={reminderForm.title}
                                        onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})}
                                        placeholder="e.g., Take morning medication"
                                    />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Time *</Label>
                                    <Input 
                                        type="time"
                                        value={reminderForm.time}
                                        onChange={(e) => setReminderForm({...reminderForm, time: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Repeat Days</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() => {
                                                    const days = reminderForm.days.includes(day)
                                                        ? reminderForm.days.filter(d => d !== day)
                                                        : [...reminderForm.days, day];
                                                    setReminderForm({...reminderForm, days});
                                                }}
                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all
                                                    ${reminderForm.days.includes(day) 
                                                        ? 'bg-primary text-white' 
                                                        : 'bg-muted text-muted-foreground hover:bg-primary/10'}`}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea 
                                    value={reminderForm.notes}
                                    onChange={(e) => setReminderForm({...reminderForm, notes: e.target.value})}
                                    placeholder="Additional notes for this reminder..."
                                    rows={2}
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowAddReminder(false)}>Cancel</Button>
                                <Button onClick={handleSubmitReminder} disabled={loading} className="rounded-full">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                                    Create Reminder
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Active Reminders */}
                {reminders.length > 0 && (
                    <Card className="mb-6 border-amber-200 dark:border-amber-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Bell className="w-5 h-5 text-amber-500" />
                                Active Reminders
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {reminders.map((reminder) => (
                                    <div key={reminder.id} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                                                <Bell className="w-4 h-4 text-amber-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm">{reminder.title}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {reminder.time} • {reminder.days?.length > 0 ? reminder.days.join(', ') : 'Daily'}
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => handleDeleteReminder(reminder.id)}>
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Diary Entries */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            {(() => {
                                const typeInfo = getDiaryTypeInfo(activeTab);
                                const Icon = typeInfo.icon;
                                return <Icon className={`w-5 h-5 ${typeInfo.color}`} />;
                            })()}
                            {getDiaryTypeInfo(activeTab).name} Entries
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {entries.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <BookHeart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No entries yet</p>
                                <Button 
                                    variant="outline" 
                                    className="mt-4"
                                    onClick={() => setShowAddEntry(true)}
                                >
                                    Add Your First Entry
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {entries.map((entry) => (
                                    <div key={entry.id} className={`p-4 rounded-xl border ${getDiaryTypeInfo(entry.entry_type).bgColor}`}>
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4">
                                                <div className={`w-10 h-10 rounded-lg ${getDiaryTypeInfo(entry.entry_type).bgColor} flex items-center justify-center`}>
                                                    {(() => {
                                                        const typeInfo = getDiaryTypeInfo(entry.entry_type);
                                                        const Icon = typeInfo.icon;
                                                        return <Icon className={`w-5 h-5 ${typeInfo.color}`} />;
                                                    })()}
                                                </div>
                                                <div>
                                                    <p className="font-medium">{entry.date}</p>
                                                    {entry.entry_type === 'menstrual' && (
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            <p>Flow: {entry.data?.flow_level}</p>
                                                            {entry.data?.symptoms && <p>Symptoms: {entry.data.symptoms}</p>}
                                                            {entry.data?.mood && <p>Mood: {entry.data.mood}</p>}
                                                        </div>
                                                    )}
                                                    {entry.entry_type === 'pain' && (
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            <p>Pain Level: {entry.data?.pain_level}/10 at {entry.data?.location}</p>
                                                            {entry.data?.description && <p>Type: {entry.data.description}</p>}
                                                            {entry.data?.triggers && <p>Triggers: {entry.data.triggers}</p>}
                                                        </div>
                                                    )}
                                                    {entry.entry_type === 'medication' && (
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            <p>{entry.data?.medication_name} - {entry.data?.dosage}</p>
                                                            {entry.data?.frequency && <p>Frequency: {entry.data.frequency}</p>}
                                                            {entry.data?.time_taken && <p>Taken at: {entry.data.time_taken}</p>}
                                                        </div>
                                                    )}
                                                    {entry.notes && (
                                                        <p className="text-xs text-muted-foreground mt-2 italic">"{entry.notes}"</p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
                                                        <Clock className="w-3 h-3" />
                                                        {new Date(entry.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button variant="ghost" size="icon" onClick={() => handleDeleteEntry(entry.id)}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
